import { z } from "zod";
import { projectRoleSchema } from "./project";

/**
 * One member of a project, as `GET /api/v1/projects/:id/members` returns them. Email is
 * deliberately absent: the list shows names, and invitations are a separate surface.
 * `lastActiveAt` is null when the person has not acted yet.
 */
export const projectMemberSchema = z.object({
  userId: z.uuid(),
  displayName: z.string().min(1),
  initials: z.string().min(1).max(3),
  role: projectRoleSchema,
  isSelf: z.boolean(),
  lastActiveAt: z.iso.datetime().nullable(),
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

export const projectMemberListSchema = z.array(projectMemberSchema);
export type ProjectMemberList = z.infer<typeof projectMemberListSchema>;

/** `PATCH /api/v1/projects/:id/members/:userId`. Only an owner may call it. */
export const changeRoleSchema = z.object({
  role: projectRoleSchema,
});
export type ChangeRole = z.infer<typeof changeRoleSchema>;

/**
 * `POST /api/v1/projects/:id/transfer`. Makes another member the owner and the caller a member,
 * in one transaction, so the project always keeps exactly one owner.
 */
export const transferOwnershipSchema = z.object({
  newOwnerId: z.uuid(),
});
export type TransferOwnership = z.infer<typeof transferOwnershipSchema>;

/**
 * One audit entry for a membership change. Written on every role change, removal, and transfer.
 * Kept in contracts so the members routes and the future audit page cannot drift.
 */
export const membershipAuditActions = [
  "member.role-changed",
  "member.removed",
  "ownership.transferred",
  "invitation.created",
  "invitation.resent",
  "invitation.revoked",
  "billing.settings-updated",
] as const;
export const membershipAuditActionSchema = z.enum(membershipAuditActions);
export type MembershipAuditAction = z.infer<typeof membershipAuditActionSchema>;

export const membershipAuditEntrySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  action: membershipAuditActionSchema,
  actorId: z.uuid(),
  subjectId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
});
export type MembershipAuditEntry = z.infer<typeof membershipAuditEntrySchema>;
